import { isUnassignedLane } from "@/features/calendar/constants";
import {
  type CalendarZoom,
  type CoWorkerCalendarItem,
  isCoWorkerCalendarBokaAppointment,
  isCoWorkerCalendarTask,
  isCoWorkerScheduleActivity,
} from "@/features/calendar/types";
import { type UpdateTaskData } from "@/features/calendar/updateTask";
import { allowDragAndDrop, allowResize } from "@/features/calendar/utils";
import {
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { createSnapModifier } from "@dnd-kit/modifiers";
import {
  type Appointment,
  assignAppointment,
  fetchAppointmentDetails,
  updateDropInAppointments,
  updateSchedule,
} from "@repo/api";
import { t } from "@repo/translation";
import { useToast } from "@repo/ui";
import {
  formatDateToApi,
  formatDateToApiUTC,
  getErrorMessage,
} from "@repo/utils";
import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  differenceInDays,
} from "date-fns";

import { useCalendarContext } from "../CalendarProvider";
import { getZoomHourWidth } from "../utils/utils";
import { useDragPreviewState } from "./useDragPreview";

const ROUND_TO_5_MINUTES = 1000 * 60 * 5;

const snapToGridModifier = createSnapModifier(1);

// Rounds times to even 5 minutes to make drag and drop
const getRoundedDates = (
  taskItem: CoWorkerCalendarItem & { resize?: boolean },
  dayDiff: number,
  minutesDiff: number,
  horizontalResize = false
) => {
  const resize = taskItem.resize ?? false;
  const originalDayDiff = differenceInDays(taskItem.end, taskItem.start);

  const newStartDate = resize
    ? taskItem.start
    : addDays(addMinutes(taskItem.start, minutesDiff), dayDiff);
  const newEndDate = addDays(
    addMinutes(taskItem.end, minutesDiff),
    resize ? (horizontalResize ? 0 : dayDiff - originalDayDiff) : dayDiff
  );

  const roundedStartDate = taskItem.allDay
    ? newStartDate
    : new Date(
        Math.round(newStartDate.getTime() / ROUND_TO_5_MINUTES) *
          ROUND_TO_5_MINUTES
      );
  const roundedEndDate = taskItem.allDay
    ? newEndDate
    : new Date(
        Math.round(newEndDate.getTime() / ROUND_TO_5_MINUTES) *
          ROUND_TO_5_MINUTES
      );
  return { roundedStartDate, roundedEndDate };
};

interface UseHandleDragOptions {
  onUpdateTask: (data: UpdateTaskData) => Promise<void>;
  mutateSearchAppointments?: () => void;
  // For team calendar (horizontal drag)
  totalHours?: number;
  zoom?: {
    zoom: CalendarZoom;
    zoomWidth: number;
  };
  // For week calendar (vertical drag)
  weekBlockHeight?: number;
  weekBlockWidth?: number;
}

export const useHandleDrag = ({
  onUpdateTask,
  mutateSearchAppointments,
  totalHours,
  zoom,
  weekBlockHeight,
  weekBlockWidth,
}: UseHandleDragOptions) => {
  const { updateToast } = useToast();
  const { dragPreview, setDragPreview } = useDragPreviewState();
  const { appointmentFilters } = useCalendarContext();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragMove = (event: DragMoveEvent) => {
    const item = event.active?.data
      ?.current as unknown as CoWorkerCalendarItem & { resize: boolean };

    if (!item || !allowDragAndDrop(item, appointmentFilters.tableView)) {
      return;
    }

    // Week calendar calculation (vertical drag)
    if (weekBlockHeight && weekBlockWidth) {
      const minutesDiff = (60 / weekBlockHeight) * event.delta.y;
      const dayDiff = Math.round(event.delta.x / weekBlockWidth);

      const { roundedStartDate, roundedEndDate } = getRoundedDates(
        item,
        dayDiff,
        minutesDiff
      );

      setDragPreview({
        id: item.id,
        tempStartDate: roundedStartDate,
        tempEndDate: roundedEndDate,
      });
      return;
    }

    // Team calendar calculation (horizontal drag)
    if (totalHours && zoom) {
      const minutes = (60 / getZoomHourWidth(zoom, totalHours)) * event.delta.x;

      const { roundedStartDate, roundedEndDate } = getRoundedDates(
        item,
        0, // no day diff for horizontal drag
        minutes,
        true
      );

      setDragPreview({
        id: item.id,
        tempStartDate: roundedStartDate,
        tempEndDate: roundedEndDate,
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setDragPreview(null);

    const item = event.active?.data
      ?.current as unknown as CoWorkerCalendarItem & { resize: boolean };

    if (!item || !allowDragAndDrop(item, appointmentFilters.tableView)) {
      return;
    }

    // Task

    if (!allowResize(item)) {
      return;
    }

    let roundedStartDate: Date;
    let roundedEndDate: Date;

    if (weekBlockHeight) {
      // Week calendar calculation (vertical drag)
      const updateData = event.over?.data?.current as any;

      if (!updateData && item.resize) {
        return;
      }

      if (updateData && !item.allDay && updateData.allDaySlot) {
        return;
      }

      if (item.resize && updateData) {
        const minutesDiff = (60 / weekBlockHeight) * event.delta.y;
        const dayDiff = differenceInCalendarDays(updateData.date, item.start);
        const dates = getRoundedDates(item, dayDiff, minutesDiff);
        roundedStartDate = dates.roundedStartDate;
        roundedEndDate = new Date(
          Math.round(updateData.date.getTime() / ROUND_TO_5_MINUTES) *
            ROUND_TO_5_MINUTES
        );
      } else {
        const minutesDiff = (60 / weekBlockHeight) * event.delta.y;
        const dayDiff = Math.round(event.delta.x / weekBlockWidth!);
        const dates = getRoundedDates(item, dayDiff, minutesDiff);

        roundedStartDate = dates.roundedStartDate;
        roundedEndDate = dates.roundedEndDate;
      }
    } else if (totalHours && zoom) {
      // Team calendar calculation (horizontal drag)
      const minutes = (60 / getZoomHourWidth(zoom, totalHours)) * event.delta.x;

      const dates = getRoundedDates(item, 0, minutes, true);
      roundedStartDate = dates.roundedStartDate;
      roundedEndDate = dates.roundedEndDate;
    } else {
      return;
    }

    const dropData = event.over?.data?.current as UpdateTaskData | undefined;
    const emailId = dropData?.emailId ?? item.emailId;
    const assignedTeam = dropData?.assignedTeam ?? item.assignedTeam;

    // Task
    if (isCoWorkerCalendarTask(item)) {
      await onUpdateTask?.({
        id: item.id,
        emailId,
        assignedTeam,
        startDate: item.allDay
          ? formatDateToApi(item.start)
          : formatDateToApi(roundedStartDate),
        endDate: item.allDay
          ? formatDateToApi(item.end)
          : formatDateToApi(roundedEndDate),
      });
    }

    // Appointment
    else if (isCoWorkerCalendarBokaAppointment(item)) {
      if (item.isDropIn) {
        const isUnassigned = emailId ? isUnassignedLane(emailId) : false;
        try {
          const newAssignedCoworker = isUnassigned ? undefined : emailId;
          const newAssignedTeam = isUnassigned
            ? (assignedTeam ?? item.assignedTeam)
            : undefined;

          const fullAppointment = await fetchAppointmentDetails({
            id: item.id,
            type: "DROP_IN",
          });

          await updateDropInAppointments(
            {
              ...fullAppointment,
              id: item.id,
              opportunityId:
                fullAppointment?.opportunityId ?? item.opportunityId,
              appointmentStatus:
                fullAppointment?.appointmentStatus ?? item.status!,
              locationType: fullAppointment?.locationType ?? item.locationType,
              service: fullAppointment?.service ?? item.service ?? "",
              locationName:
                fullAppointment?.locationName ?? item.location ?? "",
              type: "DROP_IN",
              appointmentType: "DROP_IN",
              price: fullAppointment?.price ?? 0,
              customer:
                fullAppointment?.customer ??
                (item.customer as Appointment["customer"]),
              orderNumber: fullAppointment?.orderNumber ?? item.orderNumber,
              videoLinkCoworker:
                fullAppointment?.videoLinkCoworker ?? item.videoLinkCoworker,
              assignedTeam: fullAppointment?.assignedTeam ?? item.assignedTeam,
              assignedCoworker:
                fullAppointment?.assignedCoworker ?? item.emailId,
              timeWindowStartTime: formatDateToApiUTC(roundedStartDate),
              timeWindowEndTime: formatDateToApiUTC(roundedEndDate),
              ...(newAssignedCoworker !== undefined || emailId
                ? {
                    assignedCoworker: newAssignedCoworker,
                    assignedTeam: newAssignedTeam,
                  }
                : {}),
            },
            mutateSearchAppointments
          );
          updateToast({
            id: item.id,
            title: t("prompt.appointment_reassigned"),
          });
        } catch (err) {
          updateToast({
            id: item.id,
            title: t("prompt.appointment_reassign_failed"),
            message: getErrorMessage(err),
            type: "error",
          });
        }
      } else {
        try {
          const result = await assignAppointment(
            Number(item.id),
            item.opportunityId,
            item.emailId,
            emailId,
            mutateSearchAppointments
          );

          if (result) {
            updateToast({
              id: item.id,
              title: t("prompt.appointment_assigned"),
            });
          }
        } catch (err) {
          updateToast({
            id: item.id,
            title: t("prompt.appointment_assign_failed"),
            message: getErrorMessage(err),
            type: "error",
          });
        }
      }
    }

    // Schedule
    else if (isCoWorkerScheduleActivity(item)) {
      try {
        await updateSchedule(item.id, {
          shiftStartTime: formatDateToApiUTC(roundedStartDate),
          shiftEndTime: formatDateToApiUTC(roundedEndDate),
          coworkerEmail: emailId,
        });
        updateToast({
          id: item.id,
          title: t("prompt.schedule_updated"),
        });
      } catch (err) {
        updateToast({
          id: item.id,
          title: t("prompt.schedule_update_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      }
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const item = event.active?.data?.current as CoWorkerCalendarItem;

    if (!allowDragAndDrop(item, appointmentFilters.tableView)) {
      updateToast({
        id: item.id,
        title: t("prompt.drag_not_possible_title"),
        message: t("prompt.drag_not_possible_message"),
      });
    }
  };

  return {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    snapToGridModifier,
    sensors,
    dragPreview,
  };
};
